import { Card, CardBody } from '@nextui-org/react';
import {
    AddConfigurationFieldForm,
    CtyButton,
    CtySpinner,
    formatKeyForDisplay,
    NavSidebar,
    SimpleDeleteConfirmationModal,
} from '@circuitry-ai/doc-ui';
import { useMemo } from 'react';
import { getAddFieldPathKey, passthroughLabel, type DifyConfigurationProps } from './difyConfigurationUtils';
import { DifySelectedNodeEditor, type DifyNestedEditorModel } from './DifyNestedEditor';
import { useDifyConfiguration } from './useDifyConfiguration';

export type { DifyConfigurationProps };

export function DifyConfiguration(props: Readonly<DifyConfigurationProps>) {
    const configuration = useDifyConfiguration(props);
    const {
        aiworkerId,
        isCollapsed,
        setIsCollapsed,
        configState,
        currentSection,
        setCurrentSection,
        sectionKeys,
        hasSections,
        showEmptyConfigurationState,
        isLoading,
        isFetching,
        isError,
        isConfigurationEditable,
        requiresCreateConfiguration,
        patchConfigurationId,
        flatUpdates,
        isSaving,
        isAddingField,
        addFieldDraftByPath,
        openAddFieldDraft,
        clearAddFieldDraft,
        updateAddFieldDraft,
        addFieldAtPath,
        handleResetAll,
        handleSave,
        handleToggleExpandAll,
        expandAll,
        isDeleteModalOpen,
        closeDeleteModal,
        pendingDeletePath,
        difyDeletePathPrefix,
        handleConfirmDelete,
        isDeleting,
    } = configuration;

    const navGroups = useMemo(
        () => [
            {
                heading: null,
                items: sectionKeys.map((key) => ({
                    key,
                    path: `/agents/dify-config/${key}`,
                    label: formatKeyForDisplay(key),
                    icon: key.toLowerCase().includes('prompt') ? 'mdi:text-box-search-outline' : 'mdi:layers-outline',
                })),
            },
        ],
        [sectionKeys]
    );

    const selectedLabel = navGroups[0]?.items.find((item) => item.key === currentSection)?.label ?? 'Workflow Config';
    const selectedNodePath: string[] = currentSection ? [currentSection] : [];
    const selectedNode = currentSection ? configState[currentSection] : undefined;

    const editorModel: DifyNestedEditorModel = {
        isConfigurationEditable,
        requiresCreateConfiguration,
        expandAll,
        accordionVersion: configuration.accordionVersion,
        initialConfigState: configuration.initialConfigState,
        previewModeByPath: configuration.previewModeByPath,
        addFieldDraftByPath,
        isAddingField,
        updateField: configuration.updateField,
        handleFieldDelete: configuration.handleFieldDelete,
        setPreviewModeByPath: configuration.setPreviewModeByPath,
        openAddFieldDraft,
        clearAddFieldDraft,
        updateAddFieldDraft,
        addFieldAtPath,
    };

    const renderAddRootConfigurationControls = (variant: 'empty' | 'inline') => {
        if (!isConfigurationEditable) return null;
        const rootPathKey = getAddFieldPathKey([]);
        const rootDraft = addFieldDraftByPath[rootPathKey];

        if (!rootDraft) {
            return (
                <div className={variant === 'empty' ? 'flex flex-col items-center gap-3 py-6' : 'flex justify-end'}>
                    {variant === 'empty' ? (
                        <p className="text-sm text-default-500">No workflow configuration found for this agent.</p>
                    ) : null}
                    <CtyButton
                        size="sm"
                        color="primary"
                        variant="solid"
                        onPress={() => openAddFieldDraft([])}
                        isDisabled={isAddingField}
                    >
                        Add Configuration
                    </CtyButton>
                </div>
            );
        }

        return (
            <AddConfigurationFieldForm
                draft={rootDraft}
                isDisabled={!isConfigurationEditable}
                isAdding={isAddingField}
                showConfigId={requiresCreateConfiguration}
                onDraftChange={(patch) => updateAddFieldDraft([], (draft) => ({ ...draft, ...patch }))}
                onCancel={() => clearAddFieldDraft([])}
                onAdd={() => addFieldAtPath([])}
                t={passthroughLabel}
            />
        );
    };

    const renderEditorContent = () => {
        if (!aiworkerId) {
            return <div className="text-sm text-default-500">AI Worker ID is missing for this agent.</div>;
        }
        if (isLoading || isFetching) {
            return <CtySpinner size="lg" />;
        }
        if (isError) {
            return <div className="text-sm text-danger">Failed to load workflow configuration.</div>;
        }
        if (showEmptyConfigurationState) {
            return renderAddRootConfigurationControls('empty');
        }
        if (!currentSection) {
            return <p className="text-sm text-default-500">Select a section from the left menu.</p>;
        }
        return (
            <DifySelectedNodeEditor
                currentSection={currentSection}
                selectedNode={selectedNode}
                selectedNodePath={selectedNodePath}
                model={editorModel}
            />
        );
    };

    return (
        <div className="flex h-full min-h-0 border border-divider overflow-hidden bg-content1">
            <NavSidebar
                navGroups={navGroups}
                currentSection={currentSection}
                isCollapsed={isCollapsed}
                navClassName="pt-0"
                onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
                headerTitle="Workflow Config"
                expandMenuLabel="Expand menu"
                collapseMenuLabel="Collapse menu"
                onInternalNavigate={(_, item) => setCurrentSection(item.key)}
            />

            <main className="flex-1 min-w-0 overflow-y-auto p-2 bg-default-50">
                <Card className="border border-divider shadow-none">
                    <CardBody className="p-0">
                        <div className="flex items-center justify-between border-b border-divider px-3 py-3">
                            <div>
                                <div className="text-lg font-semibold text-default-900">
                                    {currentSection ? selectedLabel : 'Workflow Config'}
                                </div>
                                <div className="text-sm text-default-500">Update configuration values for this section.</div>
                            </div>
                            <div className="flex items-center gap-2">
                                {hasSections ? renderAddRootConfigurationControls('inline') : null}
                                <CtyButton size="sm" variant="bordered" onPress={handleResetAll} isDisabled={!isConfigurationEditable || !hasSections}>
                                    Reset All
                                </CtyButton>
                                <CtyButton
                                    size="sm"
                                    color="primary"
                                    variant="solid"
                                    onPress={handleSave}
                                    isDisabled={!isConfigurationEditable || !patchConfigurationId || Object.keys(flatUpdates).length === 0}
                                    isLoading={isSaving}
                                >
                                    Save
                                </CtyButton>
                                <CtyButton
                                    size="sm"
                                    color="default"
                                    onPress={handleToggleExpandAll}
                                    isDisabled={!hasSections}
                                >
                                    {expandAll ? 'Collapse All' : 'Expand All'}
                                </CtyButton>
                            </div>
                        </div>
                        <div className="p-2">
                            {renderEditorContent()}
                        </div>
                    </CardBody>
                </Card>
            </main>
            <SimpleDeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closeDeleteModal();
                    }
                }}
                body={
                    <div className="flex flex-col gap-2">
                        <p className="text-sm text-default-700">
                            Are you sure you want to delete this configuration field?
                        </p>
                        {pendingDeletePath?.length ? (
                            <p className="text-xs text-default-500">{`${difyDeletePathPrefix}.${pendingDeletePath.join('.')}`}</p>
                        ) : null}
                    </div>
                }
                onConfirm={handleConfirmDelete}
                isConfirmLoading={isDeleting}
            />
        </div>
    );
}

export default DifyConfiguration;
